import { logger, roseStorage } from '@inboundx/shared/platform';
import type React from 'react';
import { useEffect, useState } from 'react';

// Key for storing parent window events override in localStorage
const PARENT_WINDOW_EVENTS_KEY = 'rose_preprod_parent_window_events_enabled';

// Key for storing in-chat booking override in localStorage
const INCHAT_BOOKING_OVERRIDE_KEY = 'rose_preprod_inchat_booking_override';

// Key for storing copilot layout override in localStorage
const COPILOT_LAYOUT_OVERRIDE_KEY = 'rose_preprod_copilot_layout_override';

// Key for storing LLM response language override in localStorage
const LLM_RESPONSE_LANGUAGE_OVERRIDE_KEY = 'rose_preprod_llm_response_language_override';

type LlmResponseLanguageOverride = 'config' | 'site_language' | 'auto';

// Key for storing the response-priority (Playbook) override in localStorage (read by the
// shared widget service and sent as `playbook` on the chat request; staff/playground only).
const PLAYBOOK_OVERRIDE_KEY = 'rose_preprod_playbook_override';

type PlaybookOverride = 'default' | 'conversion' | 'information';

/** Get the preprod LLM response language override value */
export function getPreprodLlmResponseLanguageOverride(): LlmResponseLanguageOverride {
    try {
        const value = localStorage.getItem(LLM_RESPONSE_LANGUAGE_OVERRIDE_KEY);
        if (value === 'site_language' || value === 'auto') return value;
        return 'config';
    } catch {
        return 'config';
    }
}

/** Get the preprod response-priority (Playbook) override value. Legacy self_serve/sales_led
 * values (older bookmarks) are coerced to the new names (IX-4119). */
export function getPreprodPlaybookOverride(): PlaybookOverride {
    try {
        const value = localStorage.getItem(PLAYBOOK_OVERRIDE_KEY);
        if (value === 'conversion' || value === 'sales_led') return 'conversion';
        if (value === 'information' || value === 'self_serve') return 'information';
        return 'default';
    } catch {
        return 'default';
    }
}

// Key for the demo-timing override (IX-4063), read by the shared widget service and sent
// as `demoTiming` on the chat request; staff/playground only.
const DEMO_TIMING_OVERRIDE_KEY = 'rose_preprod_demo_timing_override';

type DemoTimingOverride = 'default' | 'interest_score' | 'readiness_judge';

/** Get the preprod demo-timing override value */
export function getPreprodDemoTimingOverride(): DemoTimingOverride {
    try {
        const value = localStorage.getItem(DEMO_TIMING_OVERRIDE_KEY);
        if (value === 'interest_score' || value === 'readiness_judge') return value;
        return 'default';
    } catch {
        return 'default';
    }
}

/** Get the preprod in-chat booking override value */
function getPreprodInchatBookingOverride(): boolean | null {
    try {
        const value = localStorage.getItem(INCHAT_BOOKING_OVERRIDE_KEY);
        if (value === 'true') return true;
        if (value === 'false') return false;
        return null; // No override
    } catch {
        return null;
    }
}

/** Get the preprod copilot layout override value */
function getPreprodCopilotLayoutOverride(): boolean | null {
    try {
        const value = localStorage.getItem(COPILOT_LAYOUT_OVERRIDE_KEY);
        if (value === 'true') return true;
        if (value === 'false') return false;
        return null; // No override
    } catch {
        return null;
    }
}

/** Check if parent window events testing is enabled in preprod */
export function isPreprodParentWindowEventsEnabled(): boolean {
    try {
        return localStorage.getItem(PARENT_WINDOW_EVENTS_KEY) === 'true';
    } catch {
        return false;
    }
}

interface FeaturesTabProps {
    onConfigChange: () => void;
}

const FeaturesTab: React.FC<FeaturesTabProps> = ({ onConfigChange }) => {
    const [revealEnabled, setRevealEnabled] = useState(false);
    const [rb2bEnabled, setRb2bEnabled] = useState(false);
    const [rb2bIp, setRb2bIp] = useState('');
    const [parentWindowEventsEnabled, setParentWindowEventsEnabled] = useState(false);
    const [inchatBookingOverride, setInchatBookingOverride] = useState<
        'default' | 'enabled' | 'disabled'
    >('default');
    const [copilotLayoutOverride, setCopilotLayoutOverride] = useState<
        'default' | 'enabled' | 'disabled'
    >('default');
    const [llmResponseLanguageOverride, setLlmResponseLanguageOverride] =
        useState<LlmResponseLanguageOverride>('config');
    const [playbookOverride, setPlaybookOverride] = useState<PlaybookOverride>('default');
    const [demoTimingOverride, setDemoTimingOverride] = useState<DemoTimingOverride>('default');

    useEffect(() => {
        // Check if Reveal simulation is enabled
        try {
            setRevealEnabled(roseStorage.getRevealSimulationEnabled());
        } catch (error) {
            logger.error('Failed to load Reveal simulation status:', error);
            setRevealEnabled(false);
        }

        // Check if RB2B (US visitor) simulation is enabled
        try {
            setRb2bEnabled(roseStorage.getRb2bSimulationEnabled());
            setRb2bIp(roseStorage.getRb2bSimulationIp());
        } catch (error) {
            logger.error('Failed to load RB2B simulation status:', error);
            setRb2bEnabled(false);
        }

        // Check if parent window events testing is enabled
        setParentWindowEventsEnabled(isPreprodParentWindowEventsEnabled());

        // Check in-chat booking override
        const override = getPreprodInchatBookingOverride();
        if (override === true) setInchatBookingOverride('enabled');
        else if (override === false) setInchatBookingOverride('disabled');
        else setInchatBookingOverride('default');

        // Check copilot layout override
        const copilotOverride = getPreprodCopilotLayoutOverride();
        if (copilotOverride === true) setCopilotLayoutOverride('enabled');
        else if (copilotOverride === false) setCopilotLayoutOverride('disabled');
        else setCopilotLayoutOverride('default');

        // Check LLM response language override
        setLlmResponseLanguageOverride(getPreprodLlmResponseLanguageOverride());

        // Check response-priority (Playbook) override
        setPlaybookOverride(getPreprodPlaybookOverride());
        setDemoTimingOverride(getPreprodDemoTimingOverride());
    }, []);

    const handleToggleReveal = () => {
        const newState = !revealEnabled;
        setRevealEnabled(newState);

        try {
            if (newState) {
                // Reveal sim and RB2B sim both own window.reveal — keep them mutually exclusive.
                roseStorage.setRb2bSimulationEnabled(false);
                setRb2bEnabled(false);
                roseStorage.setRevealSimulationEnabled(true);

                const mockRevealData = {
                    ip: '203.0.113.45',
                    domain: 'example.com',
                    type: 'company',
                    company: 'Example Corporation',
                    confidenceScore: 0.92,
                    fuzzy: false,
                    geoIP: {
                        city: 'San Francisco',
                        state: 'California',
                        stateCode: 'CA',
                        country: 'United States',
                        countryCode: 'US',
                    },
                };
                window.reveal = mockRevealData;
            } else {
                roseStorage.setRevealSimulationEnabled(false);
                delete window.reveal;
            }
        } catch (error) {
            console.error('Failed to toggle Reveal simulation:', error);
        }

        onConfigChange();
    };

    const buildRb2bRevealData = (ip: string) => ({
        ip,
        domain: null,
        type: 'isp',
        company: null,
        confidenceScore: null,
        fuzzy: false,
        geoIP: {
            country: 'United States',
            countryCode: 'US',
        },
    });

    const handleToggleRb2b = () => {
        const newState = !rb2bEnabled;
        setRb2bEnabled(newState);

        try {
            if (newState) {
                // RB2B sim and Reveal sim both own window.reveal — keep them mutually exclusive.
                roseStorage.setRevealSimulationEnabled(false);
                setRevealEnabled(false);
                roseStorage.setRb2bSimulationEnabled(true);
                if (rb2bIp) {
                    window.reveal = buildRb2bRevealData(rb2bIp);
                }
            } else {
                roseStorage.setRb2bSimulationEnabled(false);
                delete window.reveal;
            }
        } catch (error) {
            logger.error('Failed to toggle RB2B simulation:', error);
        }

        onConfigChange();
    };

    const handleRb2bIpChange = (value: string) => {
        setRb2bIp(value);

        try {
            roseStorage.setRb2bSimulationIp(value);
            if (rb2bEnabled) {
                if (value) {
                    window.reveal = buildRb2bRevealData(value);
                } else {
                    delete window.reveal;
                }
            }
        } catch (error) {
            logger.error('Failed to save RB2B simulation IP:', error);
        }
    };

    const handleToggleParentWindowEvents = () => {
        const newState = !parentWindowEventsEnabled;
        setParentWindowEventsEnabled(newState);

        try {
            localStorage.setItem(PARENT_WINDOW_EVENTS_KEY, newState.toString());
            logger.debug(`Parent window events testing ${newState ? 'enabled' : 'disabled'}`);
        } catch (error) {
            logger.error('Failed to save parent window events setting:', error);
        }
    };

    const handleInchatBookingChange = (value: 'default' | 'enabled' | 'disabled') => {
        setInchatBookingOverride(value);

        try {
            if (value === 'default') {
                localStorage.removeItem(INCHAT_BOOKING_OVERRIDE_KEY);
            } else {
                localStorage.setItem(
                    INCHAT_BOOKING_OVERRIDE_KEY,
                    value === 'enabled' ? 'true' : 'false'
                );
            }
            logger.debug(`In-chat booking override set to: ${value}`);
            onConfigChange();
        } catch (error) {
            logger.error('Failed to save in-chat booking override:', error);
        }
    };

    const handleCopilotLayoutChange = (value: 'default' | 'enabled' | 'disabled') => {
        setCopilotLayoutOverride(value);

        try {
            if (value === 'default') {
                localStorage.removeItem(COPILOT_LAYOUT_OVERRIDE_KEY);
            } else {
                localStorage.setItem(
                    COPILOT_LAYOUT_OVERRIDE_KEY,
                    value === 'enabled' ? 'true' : 'false'
                );
            }
            logger.debug(`Copilot layout override set to: ${value}`);
            onConfigChange();
        } catch (error) {
            logger.error('Failed to save copilot layout override:', error);
        }
    };

    const handleLlmResponseLanguageChange = (value: LlmResponseLanguageOverride) => {
        setLlmResponseLanguageOverride(value);

        try {
            if (value === 'config') {
                localStorage.removeItem(LLM_RESPONSE_LANGUAGE_OVERRIDE_KEY);
            } else {
                localStorage.setItem(LLM_RESPONSE_LANGUAGE_OVERRIDE_KEY, value);
            }
            logger.debug(`LLM response language override set to: ${value}`);
            onConfigChange();
        } catch (error) {
            logger.error('Failed to save LLM response language override:', error);
        }
    };

    const handlePlaybookChange = (value: PlaybookOverride) => {
        setPlaybookOverride(value);

        try {
            if (value === 'default') {
                localStorage.removeItem(PLAYBOOK_OVERRIDE_KEY);
            } else {
                localStorage.setItem(PLAYBOOK_OVERRIDE_KEY, value);
            }
            logger.debug(`Response priority override set to: ${value}`);
            onConfigChange();
        } catch (error) {
            logger.error('Failed to save response priority override:', error);
        }
    };

    const handleDemoTimingChange = (value: DemoTimingOverride) => {
        setDemoTimingOverride(value);

        try {
            if (value === 'default') {
                localStorage.removeItem(DEMO_TIMING_OVERRIDE_KEY);
            } else {
                localStorage.setItem(DEMO_TIMING_OVERRIDE_KEY, value);
            }
            logger.debug(`Demo timing override set to: ${value}`);
            onConfigChange();
        } catch (error) {
            logger.error('Failed to save demo timing override:', error);
        }
    };

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="text-center pb-2 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-sm font-semibold text-gray-800">Testing Features</h3>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 min-h-0 overflow-y-auto py-3 space-y-2">
                {/* Event Viewer */}
                <FeatureRow
                    icon="📡"
                    title="Event Viewer"
                    enabled={parentWindowEventsEnabled}
                    onToggle={handleToggleParentWindowEvents}
                    activeColor="emerald"
                />

                {/* Browser Reveal */}
                <FeatureRow
                    icon="🔬"
                    title="Reveal Simulation"
                    enabled={revealEnabled}
                    onToggle={handleToggleReveal}
                    activeColor="purple"
                />

                {/* RB2B (US visitor) person-identity simulation */}
                <div
                    className={`rounded-lg border transition-all ${
                        rb2bEnabled ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="flex items-center justify-between p-2">
                        <div className="flex items-center gap-2">
                            <span className="text-base">🇺🇸</span>
                            <span className="text-xs font-medium text-gray-800">
                                RB2B US Visitor
                            </span>
                            {rb2bEnabled && (
                                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-500 text-white">
                                    ON
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={rb2bEnabled}
                            onClick={handleToggleRb2b}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
                                rb2bEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                        >
                            <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    rb2bEnabled ? 'translate-x-4' : 'translate-x-0'
                                }`}
                            />
                        </button>
                    </div>
                    {rb2bEnabled && (
                        <div className="px-2 pb-2">
                            <input
                                type="text"
                                value={rb2bIp}
                                onChange={(e) => handleRb2bIpChange(e.target.value)}
                                placeholder="US IPv4 RB2B can match (e.g. 98.0.0.1)"
                                className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <p className="text-[10px] text-gray-500 mt-1">
                                Injects window.reveal with this IP so the impression triggers the
                                RB2B person-identity waterfall. Needs person_identity enabled for
                                the site; check results in the backoffice.
                            </p>
                        </div>
                    )}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 pt-2 mt-2">
                    <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Feature Overrides
                    </p>
                </div>

                {/* In-Chat Booking Override */}
                <div
                    className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                        inchatBookingOverride !== 'default'
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-base">📅</span>
                        <span className="text-xs font-medium text-gray-800">In-Chat Booking</span>
                        {inchatBookingOverride !== 'default' && (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                                {inchatBookingOverride === 'enabled' ? 'ON' : 'OFF'}
                            </span>
                        )}
                    </div>
                    <select
                        value={inchatBookingOverride}
                        onChange={(e) =>
                            handleInchatBookingChange(
                                e.target.value as 'default' | 'enabled' | 'disabled'
                            )
                        }
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="default">Config</option>
                        <option value="enabled">ON</option>
                        <option value="disabled">OFF</option>
                    </select>
                </div>

                {/* Copilot Layout Override */}
                <div
                    className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                        copilotLayoutOverride !== 'default'
                            ? 'bg-cyan-50 border-cyan-200'
                            : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-base">📐</span>
                        <span className="text-xs font-medium text-gray-800">Copilot Layout</span>
                        {copilotLayoutOverride !== 'default' && (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-cyan-500 text-white">
                                {copilotLayoutOverride === 'enabled' ? 'ON' : 'OFF'}
                            </span>
                        )}
                    </div>
                    <select
                        value={copilotLayoutOverride}
                        onChange={(e) =>
                            handleCopilotLayoutChange(
                                e.target.value as 'default' | 'enabled' | 'disabled'
                            )
                        }
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="default">Config</option>
                        <option value="enabled">ON</option>
                        <option value="disabled">OFF</option>
                    </select>
                </div>

                {/* LLM Response Language Override */}
                <div
                    className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                        llmResponseLanguageOverride !== 'config'
                            ? 'bg-violet-50 border-violet-200'
                            : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-base">🌐</span>
                        <span className="text-xs font-medium text-gray-800">LLM Language</span>
                        {llmResponseLanguageOverride !== 'config' && (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-violet-500 text-white">
                                {llmResponseLanguageOverride === 'auto' ? 'AUTO' : 'SITE'}
                            </span>
                        )}
                    </div>
                    <select
                        value={llmResponseLanguageOverride}
                        onChange={(e) =>
                            handleLlmResponseLanguageChange(
                                e.target.value as LlmResponseLanguageOverride
                            )
                        }
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="config">Config</option>
                        <option value="site_language">Site Language</option>
                        <option value="auto">Auto-detect</option>
                    </select>
                </div>

                {/* Playbook (playbook) Override */}
                <div
                    className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                        playbookOverride !== 'default'
                            ? 'bg-violet-50 border-violet-200'
                            : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-base">🎯</span>
                        <span className="text-xs font-medium text-gray-800">Playbook</span>
                        {playbookOverride !== 'default' && (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-violet-500 text-white">
                                {playbookOverride === 'conversion' ? 'CONVERSION' : 'INFORMATION'}
                            </span>
                        )}
                    </div>
                    <select
                        value={playbookOverride}
                        onChange={(e) => handlePlaybookChange(e.target.value as PlaybookOverride)}
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="default">Config</option>
                        <option value="conversion">Force conversion-first</option>
                        <option value="information">Force information-first</option>
                    </select>
                </div>

                {/* Demo Timing Override (IX-4063) */}
                <div
                    className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                        demoTimingOverride !== 'default'
                            ? 'bg-violet-50 border-violet-200'
                            : 'bg-white border-gray-200'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-base">⏱️</span>
                        <span className="text-xs font-medium text-gray-800">Demo Timing</span>
                        {demoTimingOverride !== 'default' && (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-violet-500 text-white">
                                {demoTimingOverride === 'readiness_judge' ? 'JUDGE' : 'SCORE'}
                            </span>
                        )}
                    </div>
                    <select
                        value={demoTimingOverride}
                        onChange={(e) =>
                            handleDemoTimingChange(e.target.value as DemoTimingOverride)
                        }
                        className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="default">Config</option>
                        <option value="readiness_judge">Force readiness judge</option>
                        <option value="interest_score">Force interest score</option>
                    </select>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded p-2 border border-blue-200 mt-3">
                    <p className="text-[10px] text-blue-600">
                        Settings stored in localStorage. Changes take effect immediately.
                    </p>
                </div>
            </div>
        </div>
    );
};

interface FeatureRowProps {
    icon: string;
    title: string;
    enabled: boolean;
    onToggle: () => void;
    activeColor: 'emerald' | 'purple' | 'blue';
}

function FeatureRow({ icon, title, enabled, onToggle, activeColor }: FeatureRowProps) {
    const colorClasses = {
        emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', toggle: 'bg-emerald-500' },
        purple: { bg: 'bg-purple-50', border: 'border-purple-200', toggle: 'bg-purple-500' },
        blue: { bg: 'bg-blue-50', border: 'border-blue-200', toggle: 'bg-blue-500' },
    };

    const colors = colorClasses[activeColor];

    return (
        <div
            className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                enabled ? `${colors.bg} ${colors.border}` : 'bg-white border-gray-200'
            }`}
        >
            <div className="flex items-center gap-2">
                <span className="text-base">{icon}</span>
                <span className="text-xs font-medium text-gray-800">{title}</span>
                {enabled && (
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white">
                        ON
                    </span>
                )}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={onToggle}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
                    enabled ? colors.toggle : 'bg-gray-300'
                }`}
            >
                <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        enabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                />
            </button>
        </div>
    );
}

export default FeaturesTab;
